import React, { useState, useMemo, useCallback } from 'react';
import MapContainer from '../Map/MapContainer';
import StationMarker from '../Map/StationMarker';
import VisualClusterMarker from '../Map/VisualClusterMarker';
import LocationMarker from '../Map/LocationMarker';
import { formatPrice, formatDistance, formatTravelTime } from '@/utils/formatters';
import type { StationRecommendation, SearchFilters, Location, ClusterData } from '@/types';
import type { VisualClusterData } from '@/utils/visualClustering';

// ── Brand colours ─────────────────────────────────────────────────────────────

const BRAND_COLOURS: Record<string, string> = {
  'Costco': '#005DAA', 'ASDA': '#7BB142', 'Asda': '#7BB142',
  "Sainsbury's": '#F06C00', 'Tesco': '#005EB8', 'Morrisons': '#00543C',
  'BP': '#009639', 'Shell': '#FCC200', 'Texaco': '#E32119',
  'Esso': '#E10A1F', 'Jet': '#FFD200', 'Applegreen': '#3FA535',
  'Murco': '#0E4DA4', 'Gulf': '#F47920',
};

function brandColour(brand: string): string {
  return BRAND_COLOURS[brand] || BRAND_COLOURS[brand.toUpperCase()] || '#5a6173';
}

function buildDirectionsUrl(dest: Location, origin?: Location | null): string {
  const d = `${dest.latitude},${dest.longitude}`;
  if (origin) {
    return `https://www.google.com/maps/dir/?api=1&origin=${origin.latitude},${origin.longitude}&destination=${d}&travelmode=driving`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${d}&travelmode=driving`;
}

// ── Icon primitives ───────────────────────────────────────────────────────────

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const MapPinIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

const SlidersIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
    <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
    <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/>
    <line x1="17" y1="16" x2="23" y2="16"/>
  </svg>
);

const ListIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const MapIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
    <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
  </svg>
);

const RouteIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/>
    <circle cx="18" cy="5" r="3"/>
  </svg>
);

// ── Subcomponents ─────────────────────────────────────────────────────────────

function Sep() {
  return <div style={{ width: 1, height: 20, background: 'var(--line-strong)', flexShrink: 0 }}/>;
}

function MiniStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, marginTop: 2, color: color || 'var(--fg)' }}>{value}</div>
    </div>
  );
}

function MobileStationCard({
  recommendation, rank, cheapest, onSelect,
}: {
  recommendation: StationRecommendation;
  rank: number;
  cheapest: boolean;
  onSelect: (r: StationRecommendation) => void;
}) {
  const { station, bestPrice, distance, travelTime, totalCost } = recommendation;
  const price = bestPrice?.price_pence;

  return (
    <div
      onClick={() => onSelect(recommendation)}
      style={{
        background: cheapest
          ? 'linear-gradient(180deg, #0d1f1a, #0a1611)'
          : 'var(--ink-1)',
        border: `1px solid ${cheapest ? '#22d39a30' : 'var(--line-soft)'}`,
        borderRadius: 14, padding: 14, marginTop: 10,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
          color: cheapest ? 'var(--green)' : 'var(--fg-dim)', minWidth: 20,
        }}>
          {String(rank).padStart(2, '0')}
        </span>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: brandColour(station.brand),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0,
        }}>
          {station.brand[0]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{station.brand}</span>
            {cheapest && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: 'var(--green-soft)', color: 'var(--green)', letterSpacing: '0.06em',
                border: '1px solid #22d39a30',
              }}>CHEAPEST</span>
            )}
          </div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-dim)',
            marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {formatDistance(distance)}
            {travelTime ? ` · ${formatTravelTime(travelTime)}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 19, fontWeight: 700,
            color: cheapest ? 'var(--green)' : 'var(--fg)', lineHeight: 1, letterSpacing: '-0.01em',
          }}>
            {price !== undefined ? formatPrice(price) : 'N/A'}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-dim)', marginTop: 3 }}>
            £{(totalCost / 100).toFixed(2)} total
          </div>
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {station.address}
      </div>
    </div>
  );
}

// ── Map panel ─────────────────────────────────────────────────────────────────

interface MapPanelProps {
  recommendations: StationRecommendation[];
  selectedStation: StationRecommendation | null;
  onStationSelect: (r: StationRecommendation) => void;
  mapCenter: Location;
  targetZoom: number;
  onMapZoomChange: (z: number) => void;
  onMapBoundsChange: (b: { south: number; north: number; west: number; east: number }) => void;
  shouldUpdateView: boolean;
  p25FuelPrice: number;
  p75FuelPrice: number;
  priceRange?: { min: number; max: number };
  visualClusters: VisualClusterData[];
  individualStations: StationRecommendation[];
  shouldShowVisualClustering: boolean;
  shouldShowPersonalisedStations: boolean;
  selectedLocation: Location | null;
  locationDisplayName: string | null;
  filters: SearchFilters;
  cheapestStation: StationRecommendation | null;
  onClusterClick: (cluster: ClusterData | VisualClusterData) => void;
  onUserDrag: () => void;
}

function MobileMapPanel(props: MapPanelProps) {
  const {
    recommendations, selectedStation, onStationSelect, mapCenter, targetZoom,
    onMapZoomChange, onMapBoundsChange, shouldUpdateView, p25FuelPrice, p75FuelPrice,
    priceRange, visualClusters, individualStations, shouldShowVisualClustering,
    shouldShowPersonalisedStations, selectedLocation, locationDisplayName, filters,
    cheapestStation, onClusterClick, onUserDrag,
  } = props;

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      <MapContainer
        center={mapCenter}
        zoom={targetZoom}
        height="100%"
        className="w-full h-full"
        onZoomChange={onMapZoomChange}
        onBoundsChange={onMapBoundsChange}
        onUserDrag={onUserDrag}
        shouldUpdateView={shouldUpdateView}
      >
        {selectedStation && (
          <StationMarker
            key={`m-sel-${selectedStation.station.id}`}
            recommendation={selectedStation}
            isSelected={true}
            onClick={onStationSelect}
            tripType={filters.tripType}
            selectedLocation={selectedLocation}
            p25FuelPrice={p25FuelPrice}
            p75FuelPrice={p75FuelPrice}
          />
        )}
        {shouldShowVisualClustering && visualClusters.map(cluster => (
          <VisualClusterMarker
            key={`m-vc-${cluster.id}`}
            cluster={cluster}
            onClick={onClusterClick}
            priceRange={priceRange}
          />
        ))}
        {shouldShowVisualClustering && individualStations
          .filter(r => r.station.id !== selectedStation?.station.id)
          .map(r => (
            <StationMarker
              key={`m-ind-${r.station.id}`}
              recommendation={r}
              isSelected={false}
              onClick={onStationSelect}
              tripType={filters.tripType}
              selectedLocation={selectedLocation}
              p25FuelPrice={p25FuelPrice}
              p75FuelPrice={p75FuelPrice}
            />
          ))}
        {shouldShowPersonalisedStations && recommendations
          .filter(r => r.station.id !== selectedStation?.station.id)
          .map(r => (
            <StationMarker
              key={`m-rec-${r.station.id}`}
              recommendation={r}
              isSelected={false}
              onClick={onStationSelect}
              tripType={filters.tripType}
              selectedLocation={selectedLocation}
              p25FuelPrice={p25FuelPrice}
              p75FuelPrice={p75FuelPrice}
            />
          ))}
        {selectedLocation && (
          <LocationMarker location={selectedLocation} displayName={locationDisplayName ?? undefined} />
        )}
      </MapContainer>

      {/* Floating cheapest card */}
      {cheapestStation && (
        <div style={{
          position: 'absolute', left: 12, right: 12, bottom: 12, zIndex: 20,
          background: 'linear-gradient(180deg, #0d1f1a, #0a1611)',
          border: '1px solid #22d39a30', borderRadius: 12, padding: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: brandColour(cheapestStation.station.brand),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 800, color: '#fff', flexShrink: 0,
            }}>
              {cheapestStation.station.brand[0]}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {cheapestStation.station.brand}
                <span style={{ color: 'var(--green)', fontSize: 10, marginLeft: 6, fontWeight: 700 }}>CHEAPEST</span>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-mute)', marginTop: 1 }}>
                {formatDistance(cheapestStation.distance)}
                {cheapestStation.travelTime ? ` · ${formatTravelTime(cheapestStation.travelTime)}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: 'var(--green)', lineHeight: 1 }}>
                {cheapestStation.bestPrice?.price_pence !== undefined
                  ? formatPrice(cheapestStation.bestPrice.price_pence)
                  : 'N/A'}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', marginTop: 2 }}>
                £{(cheapestStation.totalCost / 100).toFixed(2)} total
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mobile filter drawer ──────────────────────────────────────────────────────

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)',
  letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10,
};

const SECTION_STYLE: React.CSSProperties = {
  marginBottom: 22,
  paddingBottom: 22,
  borderBottom: '1px solid var(--line-soft)',
};

interface MobileFilterDrawerProps {
  filters: SearchFilters;
  fuelAmount: number;
  averageFuelPrice: number;
  costPerMile: number;
  onFilterChange: (f: Partial<SearchFilters>) => void;
  onFuelAmountChange: (amount: number) => void;
  onCostPerMileChange: (cpm: number) => void;
  onClose: () => void;
}

function MobileFilterDrawer({
  filters, fuelAmount, averageFuelPrice, costPerMile,
  onFilterChange, onFuelAmountChange, onCostPerMileChange, onClose,
}: MobileFilterDrawerProps) {
  const FUEL_TYPES = [
    { code: 'B7', label: 'Diesel' },
    { code: 'E10', label: 'Petrol E10' },
    { code: 'E5', label: 'Petrol E5' },
    { code: 'SDV', label: 'Premium Diesel' },
  ];

  // MPG derived from costPerMile
  const effectiveFuelPrice = averageFuelPrice > 0 ? averageFuelPrice : 145;
  const currentMpg = Math.min(100, Math.max(20, Math.round((effectiveFuelPrice * 4.546) / costPerMile)));
  const handleMpgChange = (mpg: number) => {
    onCostPerMileChange(Math.round((effectiveFuelPrice * 4.546) / mpg * 10) / 10);
  };

  // Slider fill percentages
  const fuelAmountPct = (((fuelAmount - 10) / (100 - 10)) * 100).toFixed(1);
  const mpgPct = (((currentMpg - 20) / (100 - 20)) * 100).toFixed(1);
  const radiusPct = (((filters.radius - 1) / (50 - 1)) * 100).toFixed(1);

  const chipBtn = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '8px 6px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    background: active ? 'var(--amber)' : 'var(--ink-3)',
    color: active ? '#1a1d24' : 'var(--fg-mute)',
    border: `1px solid ${active ? 'var(--amber)' : 'var(--line)'}`,
    cursor: 'pointer',
  });

  const valueSpan = (val: string) => (
    <span style={{ color: 'var(--amber)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{val}</span>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60,
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />
      <div style={{
        position: 'relative',
        background: 'var(--ink-1)', borderTop: '1px solid var(--line)',
        borderRadius: '16px 16px 0 0', padding: '20px 18px 40px',
        maxHeight: '80vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>Search Parameters</span>
          <button onClick={onClose} style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>Done</button>
        </div>

        {/* Trip type */}
        <div style={SECTION_STYLE}>
          <div style={LABEL_STYLE}>Trip Type</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['single', 'return'] as const).map(t => (
              <button key={t} onClick={() => onFilterChange({ tripType: t })} style={chipBtn(filters.tripType === t)}>
                {t === 'single' ? 'Single trip' : 'Return trip'}
              </button>
            ))}
          </div>
        </div>

        {/* Fuel type */}
        <div style={SECTION_STYLE}>
          <div style={LABEL_STYLE}>Fuel Type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {FUEL_TYPES.map(ft => (
              <button
                key={ft.code}
                onClick={() => onFilterChange({ fuelTypes: [ft.code] })}
                style={{ ...chipBtn(filters.fuelTypes.includes(ft.code)), flex: 'unset', padding: '8px 16px' }}
              >
                {ft.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fill amount */}
        <div style={SECTION_STYLE}>
          <div style={LABEL_STYLE}>Fill Amount: {valueSpan(`${fuelAmount}L`)}</div>
          <input
            type="range" min="10" max="100" step="5"
            value={fuelAmount}
            className="dark-range"
            style={{ '--val': `${fuelAmountPct}%`, width: '100%' } as React.CSSProperties}
            onChange={e => onFuelAmountChange(parseInt(e.target.value))}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--fg-dim)' }}>
            <span>10L</span><span>100L</span>
          </div>
        </div>

        {/* MPG */}
        <div style={SECTION_STYLE}>
          <div style={LABEL_STYLE}>
            Efficiency: {valueSpan(`${currentMpg} mpg`)}
            <span style={{ color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', marginLeft: 6 }}>≈ {((effectiveFuelPrice * 4.546) / currentMpg).toFixed(1)}p/mi</span>
          </div>
          <input
            type="range" min="20" max="100" step="1"
            value={currentMpg}
            className="dark-range"
            style={{ '--val': `${mpgPct}%`, width: '100%' } as React.CSSProperties}
            onChange={e => handleMpgChange(parseInt(e.target.value))}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--fg-dim)' }}>
            <span>20 mpg</span><span>100 mpg</span>
          </div>
        </div>

        {/* Radius */}
        <div style={SECTION_STYLE}>
          <div style={LABEL_STYLE}>Search Radius: {valueSpan(`${filters.radius}mi`)}</div>
          <input
            type="range" min="1" max="50" step="1"
            value={filters.radius}
            className="dark-range"
            style={{ '--val': `${radiusPct}%`, width: '100%' } as React.CSSProperties}
            onChange={e => onFilterChange({ radius: parseInt(e.target.value) })}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--fg-dim)' }}>
            <span>1mi</span><span>50mi</span>
          </div>
        </div>

        {/* Sort by */}
        <div>
          <div style={LABEL_STYLE}>Sort By</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {([
              { value: 'total_cost', label: 'Total Cost' },
              { value: 'price',      label: 'Fuel Price' },
              { value: 'distance',   label: 'Distance'   },
              { value: 'savings',    label: 'Savings'    },
            ] as const).map(o => (
              <button
                key={o.value}
                onClick={() => onFilterChange({ sortBy: o.value })}
                style={{ ...chipBtn(filters.sortBy === o.value), flex: 'unset', padding: '8px 14px' }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface MobileResultsViewProps {
  recommendations: StationRecommendation[];
  selectedStation: StationRecommendation | null;
  filters: SearchFilters;
  fuelAmount: number;
  averageFuelPrice: number;
  costPerMile: number;
  locationDisplayName: string | null;
  selectedLocation: Location | null;
  minFuelPrice: number;
  maxFuelPrice: number;
  onStationSelect: (r: StationRecommendation) => void;
  onFilterChange: (f: Partial<SearchFilters>) => void;
  onFuelAmountChange: (amount: number) => void;
  onCostPerMileChange: (cpm: number) => void;
  onReset: () => void;
  isLoading: boolean;
  mapCenter: Location;
  targetZoom: number;
  onMapZoomChange: (z: number) => void;
  onMapBoundsChange: (b: { south: number; north: number; west: number; east: number }) => void;
  shouldUpdateMapView: boolean;
  p25FuelPrice: number;
  p75FuelPrice: number;
  priceRange?: { min: number; max: number };
  visualClusters: VisualClusterData[];
  individualStations: StationRecommendation[];
  shouldShowVisualClustering: boolean;
  shouldShowPersonalisedStations: boolean;
  onClusterClick: (cluster: ClusterData | VisualClusterData) => void;
  onUserDrag: () => void;
}

type SortKey = 'total' | 'price' | 'distance';

export function MobileResultsView({
  recommendations, selectedStation, filters, fuelAmount, averageFuelPrice, costPerMile,
  locationDisplayName, selectedLocation, minFuelPrice, maxFuelPrice,
  onStationSelect, onFilterChange, onFuelAmountChange, onCostPerMileChange,
  onReset, isLoading, mapCenter, targetZoom, onMapZoomChange, onMapBoundsChange,
  shouldUpdateMapView, p25FuelPrice, p75FuelPrice, priceRange, visualClusters,
  individualStations, shouldShowVisualClustering, shouldShowPersonalisedStations,
  onClusterClick, onUserDrag,
}: MobileResultsViewProps) {
  const [tab, setTab] = useState<'list' | 'map'>('map');
  const [sort, setSort] = useState<SortKey>('total');
  const [showFilters, setShowFilters] = useState(false);

  const sorted = useMemo(() => {
    const arr = [...recommendations];
    if (sort === 'price') arr.sort((a, b) => (a.bestPrice?.price_pence ?? Infinity) - (b.bestPrice?.price_pence ?? Infinity));
    else if (sort === 'distance') arr.sort((a, b) => a.distance - b.distance);
    else arr.sort((a, b) => a.totalCost - b.totalCost);
    return arr;
  }, [recommendations, sort]);

  const cheapestStation = useMemo(() => sorted[0] ?? null, [sorted]);

  const handleStationSelect = useCallback((r: StationRecommendation) => {
    onStationSelect(r);
  }, [onStationSelect]);

  return (
    <div
      className="lg:hidden flex flex-col"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'var(--ink-0)', color: 'var(--fg)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* ── Sticky top bar ── */}
      <div style={{
        background: 'rgba(7,9,15,0.94)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid var(--line-soft)',
        padding: '10px 14px 0', flexShrink: 0,
      }}>
        {/* Row 1: back / location / filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onReset}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--ink-3)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg)', flexShrink: 0,
            }}
          >
            <ChevronLeft />
          </button>
          <div style={{
            flex: 1, height: 36, padding: '0 12px',
            background: 'var(--ink-3)', border: '1px solid var(--line)',
            borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8,
            overflow: 'hidden',
          }}>
            <MapPinIcon />
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {locationDisplayName || 'Your location'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--fg-dim)', flexShrink: 0 }}>
              {filters.radius}mi
            </span>
          </div>
          <button
            onClick={() => setShowFilters(true)}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'var(--ink-3)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--fg-mute)', flexShrink: 0,
            }}
          >
            <SlidersIcon />
          </button>
        </div>

        {/* Row 2: stats strip */}
        <div style={{
          display: 'flex', gap: 14, marginTop: 12, alignItems: 'center',
          overflowX: 'auto', paddingBottom: 2,
          scrollbarWidth: 'none',
        }}>
          {minFuelPrice > 0 && (
            <>
              <MiniStat label="Cheapest" value={`${formatPrice(minFuelPrice)}/L`} color="var(--green)" />
              <Sep />
              <MiniStat label="Avg" value={`${formatPrice(averageFuelPrice)}/L`} />
              <Sep />
              <MiniStat label="Most" value={`${formatPrice(maxFuelPrice)}/L`} color="var(--red)" />
              <Sep />
            </>
          )}
          <MiniStat label="Stations" value={String(recommendations.length)} />
        </div>

        {/* Row 3: sort + view toggle */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, marginBottom: 10, alignItems: 'center' }}>
          {/* Sort segmented */}
          <div style={{
            flex: 1, display: 'inline-flex', padding: 3, gap: 2,
            background: 'var(--ink-3)', border: '1px solid var(--line)', borderRadius: 9,
          }}>
            {([
              { id: 'total' as SortKey, label: 'Total cost' },
              { id: 'price' as SortKey, label: '£/L' },
              { id: 'distance' as SortKey, label: 'Distance' },
            ]).map(o => (
              <button
                key={o.id}
                onClick={() => setSort(o.id)}
                style={{
                  flex: 1, padding: '5px 4px', fontSize: 11.5, fontWeight: 600, borderRadius: 6,
                  background: sort === o.id ? 'var(--ink-1)' : 'transparent',
                  color: sort === o.id ? 'var(--fg)' : 'var(--fg-mute)',
                  border: 'none', cursor: 'pointer',
                  boxShadow: sort === o.id ? '0 1px 0 rgba(255,255,255,0.04) inset' : 'none',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
          {/* List/Map toggle */}
          <div style={{
            display: 'inline-flex', padding: 3, gap: 2,
            background: 'var(--ink-3)', border: '1px solid var(--line)', borderRadius: 9,
          }}>
            {([
              { id: 'list' as const, icon: <ListIcon /> },
              { id: 'map' as const, icon: <MapIcon /> },
            ]).map(o => (
              <button
                key={o.id}
                onClick={() => setTab(o.id)}
                style={{
                  width: 36, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: tab === o.id ? 'var(--ink-1)' : 'transparent',
                  color: tab === o.id ? 'var(--fg)' : 'var(--fg-mute)',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
              >
                {o.icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content area ── */}
      {tab === 'map' ? (
        <MobileMapPanel
          recommendations={sorted}
          selectedStation={selectedStation}
          onStationSelect={handleStationSelect}
          mapCenter={mapCenter}
          targetZoom={targetZoom}
          onMapZoomChange={onMapZoomChange}
          onMapBoundsChange={onMapBoundsChange}
          shouldUpdateView={shouldUpdateMapView}
          p25FuelPrice={p25FuelPrice}
          p75FuelPrice={p75FuelPrice}
          priceRange={priceRange}
          visualClusters={visualClusters}
          individualStations={individualStations}
          shouldShowVisualClustering={shouldShowVisualClustering}
          shouldShowPersonalisedStations={shouldShowPersonalisedStations}
          selectedLocation={selectedLocation}
          locationDisplayName={locationDisplayName}
          filters={filters}
          cheapestStation={cheapestStation}
          onClusterClick={onClusterClick}
          onUserDrag={onUserDrag}
        />
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 14px 100px' }}>
          {isLoading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--fg-dim)', fontSize: 13 }}>
              Finding stations…
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--fg-dim)', fontSize: 13 }}>
              No stations found. Try adjusting your filters.
            </div>
          ) : (
            sorted.map((r, i) => (
              <MobileStationCard
                key={r.station.id}
                recommendation={r}
                rank={i + 1}
                cheapest={i === 0}
                onSelect={handleStationSelect}
              />
            ))
          )}
        </div>
      )}

      {/* ── Sticky bottom CTA ── */}
      {cheapestStation && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 30,
          background: 'linear-gradient(180deg, transparent, rgba(7,9,15,0.96) 28%)',
          padding: '22px 14px 20px',
        }}>
          <a
            href={buildDirectionsUrl(cheapestStation.station.location, selectedLocation)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', width: '100%', height: 50, borderRadius: 12,
              background: 'var(--amber)', color: '#1a1d24', fontWeight: 700, fontSize: 14,
              alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none',
              boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 12px 28px rgba(245,165,36,0.32)',
            }}
          >
            <RouteIcon size={15} />
            Directions to cheapest ·{' '}
            {cheapestStation.bestPrice?.price_pence !== undefined
              ? formatPrice(cheapestStation.bestPrice.price_pence)
              : 'N/A'}/L
          </a>
        </div>
      )}

      {/* ── Filter drawer ── */}
      {showFilters && (
        <MobileFilterDrawer
          filters={filters}
          fuelAmount={fuelAmount}
          averageFuelPrice={averageFuelPrice}
          costPerMile={costPerMile}
          onFilterChange={onFilterChange}
          onFuelAmountChange={onFuelAmountChange}
          onCostPerMileChange={onCostPerMileChange}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}

export default MobileResultsView;
